import React, { useState } from "react";
import { toast } from "sonner";
import { migrationApi } from "../api";

export default function ImportExportView() {
  const [json, setJson] = useState("");
  const [busy, setBusy] = useState(null);

  const handleExport = async () => {
    setBusy("export");
    try {
      const data = await migrationApi.exportAll();
      const text = JSON.stringify(data, null, 2);
      await navigator.clipboard.writeText(text);
      toast.success("Data copied to clipboard", {
        description: `${data.cars?.length || 0} cars · ${data.locations?.length || 0} locations · ${data.bookings?.length || 0} bookings`,
      });
    } catch (e) {
      toast.error("Export failed", { description: e.message });
    } finally {
      setBusy(null);
    }
  };

  const handleRecompute = async () => {
    setBusy("recompute");
    try {
      const r = await migrationApi.recomputeTaxes();
      toast.success(`Recomputed taxes on ${r.updated} bookings`);
    } catch (e) {
      toast.error("Failed", { description: e.message });
    } finally {
      setBusy(null);
    }
  };

  const handleConvert = async () => {
    setBusy("convert");
    try {
      const r = await migrationApi.normalizeImages();
      toast.success(`Images normalized (${r.cars} cars scanned)`);
    } catch (e) {
      toast.error("Failed", { description: e.message });
    } finally {
      setBusy(null);
    }
  };

  const handleImport = async () => {
    if (!json.trim()) {
      toast.error("Paste exported JSON first");
      return;
    }
    let parsed;
    try {
      parsed = JSON.parse(json);
    } catch (e) {
      toast.error("Invalid JSON");
      return;
    }
    setBusy("import");
    try {
      const r = await migrationApi.importAll(parsed);
      toast.success("Import complete", {
        description: `Cars: +${r.cars} · Locations: +${r.locations} · Bookings: +${r.bookings} · Skipped: ${r.skipped}`,
      });
      setJson("");
    } catch (e) {
      toast.error("Import failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(null);
    }
  };

  const btn = (key, label) => (busy === key ? "Working..." : label);

  return (
    <div className="space-y-5">
      <h2 className="text-xl font-bold">Data Migration</h2>

      <div className="section-card">
        <h3 className="font-bold text-lg mb-1">Step 1: Export from Preview</h3>
        <p className="muted text-sm mb-4">
          Open this page on the <span className="font-semibold text-gray-900">preview</span> admin panel and click Export. It will copy all data to your clipboard.
        </p>
        <button className="pill-btn blue" disabled={busy === "export"} onClick={handleExport}>
          {btn("export", "Export Data to Clipboard")}
        </button>
      </div>

      <div className="section-card">
        <h3 className="font-bold text-lg mb-1">Fix Booking Tax Calculations</h3>
        <p className="muted text-sm mb-4">
          If old bookings show "Tax 0%" or "$0.00 subtotal" on receipts, click below. This retroactively computes subtotal and tax amount for every booking using each booking's pickup-location tax rate. The grand total stays the same.
        </p>
        <button className="pill-btn blue" disabled={busy === "recompute"} onClick={handleRecompute}>
          {btn("recompute", "Recompute Booking Taxes")}
        </button>
      </div>

      <div className="section-card">
        <h3 className="font-bold text-lg mb-1">Fix Vehicle Images</h3>
        <p className="muted text-sm mb-4">
          If vehicle photos are showing as broken, click below. This converts file-based images into portable base64 stored in the database.
        </p>
        <button className="pill-btn blue" disabled={busy === "convert"} onClick={handleConvert}>
          {btn("convert", "Convert Images to Portable Format")}
        </button>
      </div>

      <div className="section-card">
        <h3 className="font-bold text-lg mb-1">Step 2: Import to Production</h3>
        <p className="muted text-sm mb-4">
          Open this page on the <span className="font-semibold text-gray-900">production</span> admin panel, paste the exported data below, and click Import. Duplicates are skipped.
        </p>
        <textarea
          className="input-soft"
          rows={6}
          placeholder="Paste exported JSON data here..."
          value={json}
          onChange={(e) => setJson(e.target.value)}
          style={{ fontFamily: "monospace", fontSize: "0.85rem" }}
        />
        <div className="mt-3">
          <button className="pill-btn primary" disabled={busy === "import"} onClick={handleImport}>
            {btn("import", "Import Data")}
          </button>
        </div>
      </div>

      <div className="section-card" style={{ background: "#eff6ff" }}>
        <h3 className="font-bold mb-2">How it works</h3>
        <ul className="list-disc list-inside text-sm muted space-y-1">
          <li>On <span className="font-semibold text-gray-900">Preview</span>: click <span className="font-semibold text-gray-900">Convert Images to Portable Format</span> first, then <span className="font-semibold text-gray-900">Export</span></li>
          <li>On <span className="font-semibold text-gray-900">Production</span>: Paste the JSON and click <span className="font-semibold text-gray-900">Import</span></li>
          <li>Existing cars/locations with the same name are skipped (no duplicates)</li>
          <li>Vehicle photos transfer automatically (embedded as base64 inside the JSON)</li>
        </ul>
      </div>
    </div>
  );
}
